import React, { useState, useEffect } from 'react';
import type { IconType } from 'react-icons';
import { FaInstagram, FaTiktok, FaYoutube, FaFacebook, FaGlobe } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';

const SocialIcon = ({ ic, className, style }: { ic: IconType; className?: string; style?: React.CSSProperties }) => {
  const C = ic as React.ElementType;
  return <C className={className} style={style} />;
};

interface NetworkProfile {
  talent_id: string;
  visible: boolean;
  content_about: string | null;
  instagram_followers: number | null;
  tiktok_followers: number | null;
  youtube_followers: number | null;
  twitter_followers: number | null;
  facebook_followers: number | null;
  other_followers: number | null;
  other_platform: string | null;
}

interface NetworkCreator extends NetworkProfile {
  display_name: string;
  avatar_url: string | null;
  username: string;
  instagram_handle: string | null;
  tiktok_handle: string | null;
  youtube_handle: string | null;
  twitter_handle: string | null;
  facebook_handle: string | null;
}

const PLATFORMS: { key: keyof NetworkCreator; handleKey: keyof NetworkCreator; label: string; Icon: IconType; color: string; urlFn: (h: string) => string }[] = [
  { key: 'instagram_followers', handleKey: 'instagram_handle', label: 'Instagram', Icon: FaInstagram, color: '#E1306C', urlFn: h => `https://instagram.com/${h}` },
  { key: 'tiktok_followers',    handleKey: 'tiktok_handle',    label: 'TikTok',    Icon: FaTiktok,    color: '#69C9D0', urlFn: h => `https://tiktok.com/@${h}` },
  { key: 'youtube_followers',   handleKey: 'youtube_handle',   label: 'YouTube',   Icon: FaYoutube,   color: '#FF0000', urlFn: h => `https://youtube.com/@${h}` },
  { key: 'twitter_followers',   handleKey: 'twitter_handle',   label: 'X / Twitter', Icon: FaXTwitter, color: '#ffffff', urlFn: h => `https://x.com/${h}` },
  { key: 'facebook_followers',  handleKey: 'facebook_handle',  label: 'Facebook',  Icon: FaFacebook,  color: '#1877F2', urlFn: h => `https://facebook.com/${h}` },
];

const FOLLOWER_FILTERS = [
  { label: 'Any size', min: 0 },
  { label: '1K+',      min: 1_000 },
  { label: '10K+',     min: 10_000 },
  { label: '50K+',     min: 50_000 },
  { label: '100K+',    min: 100_000 },
  { label: '500K+',    min: 500_000 },
  { label: '1M+',      min: 1_000_000 },
];

function fmtCount(n: number | null): string {
  if (!n) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return n.toLocaleString();
}

function totalFollowers(c: NetworkProfile): number {
  return (c.instagram_followers || 0) + (c.tiktok_followers || 0) +
    (c.youtube_followers || 0) + (c.twitter_followers || 0) +
    (c.facebook_followers || 0) + (c.other_followers || 0);
}

interface Props {
  talentId: string;
  onOpenChat: (channelId: string) => void;
}

export default function NetworkTab({ talentId, onOpenChat }: Props) {
  const [profile, setProfile]           = useState<NetworkProfile | null>(null);
  const [loading, setLoading]           = useState(true);
  const [showModal, setShowModal]       = useState(false);
  const [saving, setSaving]             = useState(false);
  const [connecting, setConnecting]     = useState<string | null>(null);
  const [creators, setCreators]         = useState<NetworkCreator[]>([]);
  const [connectedIds, setConnectedIds] = useState<Set<string>>(new Set());
  const [minFollowers, setMinFollowers] = useState(0);
  const [activeTab, setActiveTab]       = useState<'discover' | 'my-network'>('discover');
  const [form, setForm] = useState({
    content_about: '', instagram_followers: '', tiktok_followers: '',
    youtube_followers: '', twitter_followers: '', facebook_followers: '',
    other_platform: '', other_followers: '',
  });

  useEffect(() => {
    loadProfile();
    loadCreators();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentId]);

  const loadProfile = async () => {
    setLoading(true);
    const { data } = await supabase.from('creator_network').select('*').eq('talent_id', talentId).maybeSingle();
    setProfile(data);
    if (data) {
      setForm({
        content_about:       data.content_about       || '',
        instagram_followers: data.instagram_followers ? String(data.instagram_followers) : '',
        tiktok_followers:    data.tiktok_followers    ? String(data.tiktok_followers)    : '',
        youtube_followers:   data.youtube_followers   ? String(data.youtube_followers)   : '',
        twitter_followers:   data.twitter_followers   ? String(data.twitter_followers)   : '',
        facebook_followers:  data.facebook_followers  ? String(data.facebook_followers)  : '',
        other_platform:      data.other_platform  || '',
        other_followers:     data.other_followers ? String(data.other_followers)         : '',
      });
    }
    setLoading(false);
  };

  const loadCreators = async () => {
    // 1. Find which creators we already have a DM channel with
    const { data: myMemberships } = await supabase
      .from('chat_channel_members')
      .select('channel_id')
      .eq('talent_id', talentId);

    const myChannelIds = (myMemberships || []).map((m: any) => m.channel_id);
    let alreadyConnected = new Set<string>();

    if (myChannelIds.length > 0) {
      const { data: dmMembers } = await supabase
        .from('chat_channel_members')
        .select('channel_id, talent_id')
        .in('channel_id', myChannelIds)
        .neq('talent_id', talentId);

      // Get channels that are DM type
      const { data: dmChannels } = await supabase
        .from('chat_channels')
        .select('id')
        .in('id', myChannelIds)
        .eq('channel_type', 'dm');

      const dmChannelIdSet = new Set((dmChannels || []).map((c: any) => c.id));
      (dmMembers || []).forEach((m: any) => {
        if (dmChannelIdSet.has(m.channel_id) && m.talent_id) {
          alreadyConnected.add(m.talent_id);
        }
      });
    }
    setConnectedIds(alreadyConnected);

    // 2. Fetch network profiles for all creators that are EITHER:
    //    a) visible = true (excluding ourselves)
    //    b) OR in alreadyConnected (even if visible = false or not yet joined)
    const connectedIdsArr = Array.from(alreadyConnected);
    let netProfiles: any[] = [];
    
    try {
      let query = supabase
        .from('creator_network')
        .select('talent_id, visible, content_about, instagram_followers, tiktok_followers, youtube_followers, twitter_followers, facebook_followers, other_followers, other_platform');
      
      if (connectedIdsArr.length > 0) {
        query = query.or(`and(visible.eq.true,talent_id.neq.${talentId}),talent_id.in.(${connectedIdsArr.join(',')})`);
      } else {
        query = query.eq('visible', true).neq('talent_id', talentId);
      }
      
      const { data: netData } = await query;
      netProfiles = netData || [];
    } catch (e) {
      console.error('Error fetching creator network profiles:', e);
    }

    // 3. For any connected creator who does NOT have a row in creator_network yet, 
    //    include them as a default/empty profile so they show in "My Network".
    const netProfileMap = new Map<string, any>();
    netProfiles.forEach((n: any) => netProfileMap.set(n.talent_id, n));

    const allTalentIds = new Set<string>();
    netProfiles.forEach((n: any) => allTalentIds.add(n.talent_id));
    connectedIdsArr.forEach((id) => allTalentIds.add(id));

    if (allTalentIds.size === 0) {
      setCreators([]);
      return;
    }

    const idsToFetch = Array.from(allTalentIds);

    // 4. Fetch talent profiles + social handles in one query
    const { data: profiles } = await supabase
      .from('talent_profiles')
      .select('id, username, temp_full_name, temp_avatar_url, instagram_handle, tiktok_handle, youtube_handle, twitter_handle, facebook_handle, users!talent_profiles_user_id_fkey(full_name, avatar_url)')
      .in('id', idsToFetch);

    const profileMap = Object.fromEntries((profiles || []).map((p: any) => [p.id, p]));
    
    const creatorsList = idsToFetch.map((tId) => {
      const n = netProfileMap.get(tId) || {
        talent_id: tId,
        visible: false,
        content_about: null,
        instagram_followers: null,
        tiktok_followers: null,
        youtube_followers: null,
        twitter_followers: null,
        facebook_followers: null,
        other_followers: null,
        other_platform: null
      };
      const p = profileMap[tId];
      return {
        ...n,
        display_name:     p?.temp_full_name || p?.users?.full_name || p?.username || 'Creator',
        avatar_url:       p?.temp_avatar_url || p?.users?.avatar_url || null,
        username:         p?.username || '',
        instagram_handle: p?.instagram_handle || null,
        tiktok_handle:    p?.tiktok_handle    || null,
        youtube_handle:   p?.youtube_handle   || null,
        twitter_handle:   p?.twitter_handle   || null,
        facebook_handle:  p?.facebook_handle  || null,
      };
    }).filter(c => c.username); // Ensure we don't show invalid profiles
    
    setCreators(creatorsList);
  };

  const handleToggle = async () => {
    if (!profile?.visible) { setShowModal(true); return; }
    await supabase.from('creator_network').update({ visible: false }).eq('talent_id', talentId);
    setProfile(p => p ? { ...p, visible: false } : p);
    await loadCreators();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        talent_id:           talentId, visible: true,
        content_about:       form.content_about.trim() || null,
        instagram_followers: form.instagram_followers ? parseInt(form.instagram_followers) : null,
        tiktok_followers:    form.tiktok_followers    ? parseInt(form.tiktok_followers)    : null,
        youtube_followers:   form.youtube_followers   ? parseInt(form.youtube_followers)   : null,
        twitter_followers:   form.twitter_followers   ? parseInt(form.twitter_followers)   : null,
        facebook_followers:  form.facebook_followers  ? parseInt(form.facebook_followers)  : null,
        other_platform:      form.other_platform || null,
        other_followers:     form.other_followers ? parseInt(form.other_followers)         : null,
        updated_at:          new Date().toISOString(),
      };
      const { error } = await supabase.from('creator_network').upsert(payload, { onConflict: 'talent_id' });
      if (error) throw error;
      setProfile({ ...payload } as NetworkProfile);
      setShowModal(false);
      toast.success("You're visible to the network!");
      await loadCreators();
    } catch (e: any) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async (targetTalentId: string, targetName: string) => {
    setConnecting(targetTalentId);
    try {
      const { data, error } = await supabase.rpc('get_or_create_dm_channel', { p_talent_a: talentId, p_talent_b: targetTalentId });
      if (error) throw error;
      const channelId = data as string;
      await supabase.from('chat_channels').update({ name: `DM: ${targetName}` }).eq('id', channelId).like('name', 'dm-%');
      setConnectedIds(prev => new Set([...Array.from(prev), targetTalentId]));
      onOpenChat(channelId);
    } catch {
      toast.error('Could not open chat');
    } finally {
      setConnecting(null);
    }
  };

  const [filtersOpen, setFiltersOpen]       = useState(false);
  const [platformFilter, setPlatformFilter] = useState<string | null>(null);
  const filteredCreators = creators.filter(c => {
    if (totalFollowers(c) < minFollowers) return false;
    if (platformFilter) {
      const val = c[platformFilter as keyof NetworkCreator] as number | null;
      if (!val) return false;
    }
    // Only show visible profiles in the "Discover" tab
    return c.visible;
  });
  const myNetworkCreators = creators.filter(c => connectedIds.has(c.talent_id));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile?.visible) {
    return (
      <div className="max-w-md mx-auto text-center py-12 px-4">
        <div className="text-5xl mb-4">🤝</div>
        <h2 className="text-white font-bold text-xl mb-2">Creator Network</h2>
        <p className="text-gray-400 text-sm mb-6">Discover other ShoutOut creators for collabs, cross-promos, and partnerships. Join to see who's in the network.</p>
        <button onClick={() => setShowModal(true)} className="px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl transition-colors">
          Join the Network
        </button>
        {showModal && <Modal form={form} setForm={setForm} saving={saving} onSave={handleSave} onClose={() => setShowModal(false)} />}
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-4 items-start">

      {/* ── Sidebar: stacks above on mobile, fixed left on lg+ ── */}
      <div className="w-full lg:w-56 lg:flex-shrink-0 space-y-3">

        {/* Your profile card */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white font-semibold text-sm">Your profile</span>
            <button onClick={() => setShowModal(true)} className="text-purple-400 text-xs hover:text-purple-300">Edit</button>
          </div>
          <button onClick={handleToggle} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium border bg-purple-600 border-purple-500 text-white mb-3">
            <span className="w-3 h-3 rounded-full bg-white flex-shrink-0" />
            Visible
          </button>
          {profile.content_about && <p className="text-gray-400 text-xs leading-relaxed mb-3">{profile.content_about}</p>}
          <div className="space-y-1.5">
            {PLATFORMS.map(({ key, label, Icon, color }) => {
              const val = profile[key as keyof NetworkProfile] as number | null;
              if (!val) return null;
              return (
                <div key={String(key)} className="flex items-center gap-2">
                  <SocialIcon ic={Icon} style={{ color }} className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="text-gray-400 text-xs flex-1 truncate">{label}</span>
                  <span className="text-white text-xs font-semibold">{fmtCount(val)}</span>
                </div>
              );
            })}
            {profile.other_platform && profile.other_followers && (
              <div className="flex items-center gap-2">
                <SocialIcon ic={FaGlobe} className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
                <span className="text-gray-400 text-xs flex-1 truncate">{profile.other_platform}</span>
                <span className="text-white text-xs font-semibold">{fmtCount(profile.other_followers)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Filters — collapsible on mobile */}
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          {/* Header — always visible, tap to toggle on mobile */}
          <button
            onClick={() => setFiltersOpen(o => !o)}
            className="w-full flex items-center justify-between px-4 py-3 lg:cursor-default"
          >
            <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">
              Filters
              {(minFollowers > 0 || platformFilter) && (
                <span className="ml-2 text-purple-400 normal-case font-normal">active</span>
              )}
            </span>
            <span className={`lg:hidden text-gray-500 text-xs transition-transform ${filtersOpen ? 'rotate-180' : ''}`}>▼</span>
          </button>

          <div className={`${filtersOpen ? 'block' : 'hidden'} lg:block px-4 pb-4 space-y-4`}>
            {/* Min followers */}
            <div>
              <p className="text-gray-500 text-xs mb-2">Min reach</p>
              <div className="space-y-1">
                {FOLLOWER_FILTERS.map(f => (
                  <button key={f.min} onClick={() => setMinFollowers(f.min)}
                    className={`w-full text-left px-3 py-1.5 rounded-lg text-xs transition-colors ${minFollowers === f.min ? 'bg-purple-600 text-white font-semibold' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Platform filter */}
            <div>
              <p className="text-gray-500 text-xs mb-2">Platform</p>
              <div className="space-y-1">
                <button onClick={() => setPlatformFilter(null)}
                  className={`w-full text-left px-3 py-1.5 rounded-lg text-xs transition-colors ${!platformFilter ? 'bg-purple-600 text-white font-semibold' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
                  Any platform
                </button>
                {PLATFORMS.map(({ key, label, Icon, color }) => (
                  <button key={String(key)} onClick={() => setPlatformFilter(String(key) === platformFilter ? null : String(key))}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors ${platformFilter === String(key) ? 'bg-purple-600 text-white font-semibold' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
                    <SocialIcon ic={Icon} style={{ color: platformFilter === String(key) ? '#fff' : color }} className="w-3.5 h-3.5 flex-shrink-0" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {(minFollowers > 0 || platformFilter) && (
              <button onClick={() => { setMinFollowers(0); setPlatformFilter(null); }}
                className="w-full text-center text-xs text-gray-500 hover:text-gray-300 transition-colors pt-1">
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="flex-1 min-w-0">
        {/* Tabs */}
        <div className="flex gap-1 mb-4 bg-white/5 rounded-xl p-1 w-full">
          <button onClick={() => setActiveTab('discover')}
            className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeTab === 'discover' ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-gray-300'}`}>
            Discover
            <span className="ml-1.5 text-gray-500 font-normal">{filteredCreators.length}</span>
          </button>
          <button onClick={() => setActiveTab('my-network')}
            className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeTab === 'my-network' ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-gray-300'}`}>
            My Network
            {myNetworkCreators.length > 0 && <span className="ml-1.5 bg-purple-600 text-white text-xs px-1.5 py-0.5 rounded-full">{myNetworkCreators.length}</span>}
          </button>
        </div>

        {/* Creator cards */}
        <CreatorList
          creators={activeTab === 'discover' ? filteredCreators : myNetworkCreators}
          connectedIds={connectedIds}
          connecting={connecting}
          onConnect={handleConnect}
          emptyMsg={activeTab === 'discover'
            ? (creators.length === 0 ? 'No other creators yet.' : 'No creators match this filter.')
            : 'Connect with creators in Discover to build your network.'}
        />
      </div>

      {showModal && <Modal form={form} setForm={setForm} saving={saving} onSave={handleSave} onClose={() => setShowModal(false)} />}
    </div>
  );
}

/* ── Creator card list ────────────────────────────────────────────── */
function CreatorList({ creators, connectedIds, connecting, onConnect, emptyMsg }: {
  creators: NetworkCreator[];
  connectedIds: Set<string>;
  connecting: string | null;
  onConnect: (id: string, name: string) => void;
  emptyMsg: string;
}) {
  if (!creators.length) {
    return <div className="text-center py-10 text-gray-600 text-sm">{emptyMsg}</div>;
  }
  return (
    <div className="space-y-3">
      {creators.map(c => {
        const isConnected = connectedIds.has(c.talent_id);
        return (
          <div key={c.talent_id} className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
                {c.avatar_url
                  ? <img src={c.avatar_url} alt={c.display_name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-white font-bold text-sm">{c.display_name[0]}</div>
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm">{c.display_name}</p>
                <p className="text-gray-500 text-xs">@{c.username}</p>
              </div>
              <button
                onClick={() => !isConnected && onConnect(c.talent_id, c.display_name)}
                disabled={connecting === c.talent_id || isConnected}
                className={`flex-shrink-0 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                  isConnected
                    ? 'bg-white/5 border border-white/10 text-gray-500 cursor-default'
                    : 'bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white'
                }`}
              >
                {connecting === c.talent_id ? '…' : isConnected ? 'Connected' : 'Network'}
              </button>
            </div>

            {c.content_about && <p className="text-gray-400 text-sm mt-2.5 leading-relaxed">{c.content_about}</p>}

            <div className="flex flex-wrap gap-2 mt-2.5">
              {PLATFORMS.map(({ key, handleKey, label, Icon, color, urlFn }) => {
                const val    = c[key]    as number | null;
                const handle = c[handleKey] as string | null;
                if (!val) return null;
                const chip = (
                  <span className="flex items-center gap-1.5 px-2 py-1 bg-white/5 rounded-lg text-xs text-gray-300">
                    <SocialIcon ic={Icon} style={{ color }} className="w-3 h-3 flex-shrink-0" />
                    <span className="font-semibold text-white">{fmtCount(val)}</span>
                    <span className="text-gray-500 hidden sm:inline">{label}</span>
                  </span>
                );
                return handle ? (
                  <a key={String(key)} href={urlFn(handle)} target="_blank" rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity">
                    {chip}
                  </a>
                ) : (
                  <span key={String(key)}>{chip}</span>
                );
              })}
              {c.other_platform && c.other_followers && (
                <span className="flex items-center gap-1.5 px-2 py-1 bg-white/5 rounded-lg text-xs text-gray-300">
                  <SocialIcon ic={FaGlobe} className="w-3 h-3 flex-shrink-0 text-gray-400" />
                  <span className="font-semibold text-white">{fmtCount(c.other_followers)}</span>
                  <span className="text-gray-500 hidden sm:inline">{c.other_platform}</span>
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Modal ────────────────────────────────────────────────────────── */
function Modal({ form, setForm, saving, onSave, onClose }: {
  form: any; setForm: any; saving: boolean; onSave: () => void; onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[9999] flex flex-col justify-end sm:items-center sm:justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#111827] border border-white/10 rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm z-10 flex flex-col max-h-[92dvh]">
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 bg-white/20 rounded-full" />
        </div>
        <div className="px-5 pt-3 pb-2">
          <h3 className="text-white font-bold text-lg">Your Creator Profile</h3>
          <p className="text-gray-400 text-sm mt-0.5">Tell others who you are and your reach.</p>
        </div>
        <div className="overflow-y-auto px-5 pb-2 flex-1">
          <div className="bg-white/5 border border-white/10 rounded-xl p-3 mb-3">
            <label className="text-gray-300 text-xs font-medium block mb-2">What's your content about?</label>
            <textarea rows={3}
              placeholder="e.g. Fitness & lifestyle for moms, comedy skits, travel vlogs..."
              value={form.content_about}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm((f: any) => ({ ...f, content_about: e.target.value }))}
              className="w-full bg-transparent text-white text-sm placeholder-gray-600 focus:outline-none resize-none leading-relaxed"
            />
          </div>
          <div className="grid grid-cols-2 gap-2.5 mb-2.5">
            {PLATFORMS.map(({ key, label, Icon, color }) => (
              <div key={String(key)} className="bg-white/5 border border-white/10 rounded-xl p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <SocialIcon ic={Icon} style={{ color }} className="w-4 h-4 flex-shrink-0" />
                  <span className="text-gray-300 text-xs font-medium">{label}</span>
                </div>
                <input type="number" inputMode="numeric" min="0" placeholder="0"
                  value={form[key]}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: any) => ({ ...f, [key]: e.target.value }))}
                  className="w-full bg-transparent border-b border-white/20 py-1 text-white text-base font-semibold placeholder-gray-700 focus:outline-none focus:border-purple-500"
                />
              </div>
            ))}
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <SocialIcon ic={FaGlobe} className="w-4 h-4 text-gray-400" />
              <span className="text-gray-300 text-xs font-medium">Other</span>
            </div>
            <div className="flex gap-2">
              <input placeholder="Platform" value={form.other_platform}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: any) => ({ ...f, other_platform: e.target.value }))}
                className="flex-1 bg-transparent border-b border-white/20 py-1 text-white text-sm placeholder-gray-700 focus:outline-none focus:border-purple-500"
              />
              <input type="number" inputMode="numeric" min="0" placeholder="0" value={form.other_followers}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f: any) => ({ ...f, other_followers: e.target.value }))}
                className="w-24 bg-transparent border-b border-white/20 py-1 text-white text-base font-semibold placeholder-gray-700 focus:outline-none focus:border-purple-500 text-right"
              />
            </div>
          </div>
        </div>
        <div className="px-5 pt-3 pb-5 flex gap-3 border-t border-white/10">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-white/15 text-gray-400 text-sm font-medium hover:text-white transition-colors">Cancel</button>
          <button onClick={onSave} disabled={saving} className="flex-1 py-3 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-sm font-bold transition-colors">
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
        </div>
      </div>
    </div>
  );
}
